import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Power, MapPin, DollarSign, Star, Menu, User, 
  Wallet, Clock, LogOut, Navigation, Phone
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useRide } from '@/context/RideContext';
import { driverService } from '@/services/driverService';
import { rideService } from '@/services/rideService';
import type { Ride } from '@/types';

const DriverHome: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { acceptRide, startTracking, stopTracking } = useRide();
  
  const [isOnline, setIsOnline] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [availableRides, setAvailableRides] = useState<Ride[]>([]);
  const [currentRide, setCurrentRide] = useState<Ride | null>(null);
  const [earnings, setEarnings] = useState({ today: 0, week: 0 });

  const driver = currentUser as any;

  useEffect(() => {
    if (isOnline) {
      startTracking();
      loadAvailableRides();
      const interval = setInterval(loadAvailableRides, 10000);
      return () => {
        clearInterval(interval);
        stopTracking();
      };
    }
  }, [isOnline]);

  const loadAvailableRides = async () => {
    try {
      const rides = await rideService.getAvailableRides();
      setAvailableRides(rides);
    } catch (error) {
      console.error('Error loading rides:', error);
    }
  };

  const toggleOnlineStatus = async () => {
    const newStatus = !isOnline;
    setIsOnline(newStatus);
    if (driver) {
      await driverService.setOnlineStatus(driver.id, newStatus);
    }
  };

  const handleAcceptRide = async (rideId: string) => {
    try {
      await acceptRide(rideId);
      navigate(`/driver/ride/${rideId}`);
    } catch (error) {
      console.error('Error accepting ride:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={() => setShowMenu(true)}
            className="p-2 hover:bg-gray-100 rounded-xl"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">رحلة - سائق</h1>
          <div className="w-10" />
        </div>
      </header>

      {/* Online Toggle */}
      <div className="bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-bold text-lg">
              {isOnline ? 'متصل' : 'غير متصل'}
            </p>
            <p className="text-gray-500 text-sm">
              {isOnline ? 'جاري استقبال طلبات الرحلات' : 'اضغط للاتصال واستقبال الرحلات'}
            </p>
          </div>
          <button
            onClick={toggleOnlineStatus}
            className={`w-16 h-9 rounded-full transition-colors relative ${
              isOnline ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <div className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow transition-transform ${
              isOnline ? 'left-8' : 'left-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 p-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span className="text-gray-500 text-sm">أرباح اليوم</span>
          </div>
          <p className="text-2xl font-bold">{earnings.today} ج.س</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-5 h-5 text-yellow-500" />
            <span className="text-gray-500 text-sm">تقييمي</span>
          </div>
          <p className="text-2xl font-bold">{driver?.rating || '5.0'}</p>
        </div>
      </div>

      {/* Available Rides */}
      {isOnline && (
        <div className="flex-1 p-4">
          <h2 className="font-bold mb-4">الرحلات المتاحة</h2>
          
          {availableRides.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Navigation className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد رحلات متاحة حالياً</p>
              <p className="text-gray-400 text-sm">سيتم إشعارك عند توفر رحلات</p>
            </div>
          ) : (
            <div className="space-y-4">
              {availableRides.map((ride) => (
                <div key={ride.id} className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">نقطة الانطلاق</p>
                      <p className="font-medium">{ride.pickup?.address}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">الوجهة</p>
                      <p className="font-medium">{ride.destination?.address}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <p className="text-gray-500 text-sm">المسافة</p>
                      <p className="font-medium">{ride.estimatedDistance} كم</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">السعر</p>
                      <p className="font-bold text-primary-600">{ride.estimatedPrice} ج.س</p>
                    </div>
                    <button
                      onClick={() => handleAcceptRide(ride.id)}
                      className="bg-primary-500 text-white px-6 py-2 rounded-xl font-medium hover:bg-primary-600 transition-colors"
                    >
                      قبول
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Offline Message */}
      {!isOnline && (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Power className="w-12 h-12 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold mb-2">أنت غير متصل</h2>
            <p className="text-gray-500 mb-6">اضغط على الزر في الأعلى للاتصال واستقبال الرحلات</p>
          </div>
        </div>
      )}

      {/* Side Menu */}
      {showMenu && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl">
            <div className="p-6">
              {/* User Info */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-secondary-600" />
                </div>
                <div>
                  <p className="font-bold">{driver?.name || 'السائق'}</p>
                  <p className="text-gray-500 text-sm" dir="ltr">{driver?.phone}</p>
                </div>
              </div>

              {/* Menu Items */}
              <nav className="space-y-2">
                <button 
                  onClick={() => { navigate('/driver/profile'); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-right"
                >
                  <User className="w-5 h-5 text-gray-400" />
                  <span>الملف الشخصي</span>
                </button>
                <button 
                  onClick={() => { navigate('/driver/earnings'); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-right"
                >
                  <Wallet className="w-5 h-5 text-gray-400" />
                  <span>أرباحي</span>
                </button>
                <button 
                  onClick={() => { navigate('/driver/documents'); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 text-right"
                >
                  <Clock className="w-5 h-5 text-gray-400" />
                  <span>المستندات</span>
                </button>
              </nav>

              {/* Logout */}
              <button
                onClick={async () => {
                  await logout();
                  navigate('/');
                }}
                className="w-full mt-8 p-3 text-red-600 rounded-xl hover:bg-red-50 text-right"
              >
                تسجيل الخروج
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverHome;
